import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import * as Icons from "lucide-react";
import { ArrowRight, ShieldCheck, Wallet, FileCheck2, Scale, Search as SearchIcon, Handshake, PackageCheck, Quote } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useApi, money } from "@/lib/api";
import { GlobalSearch } from "@/components/layout/Navbar";
import { ServiceCard, ProductCard, VendorCard, ProjectCard } from "@/components/shared/Cards";
import { GridSkeleton, Stars } from "@/components/shared/common";

const Section = ({ eyebrow, title, to, children, testId }) => (
  <section className="container-x py-16 lg:py-20" data-testid={testId}>
    <div className="mb-10 flex items-end justify-between gap-6">
      <div><p className="eyebrow mb-3">{eyebrow}</p><h2 className="text-2xl font-bold tracking-tight sm:text-3xl">{title}</h2></div>
      {to && <Link to={to} className="hidden items-center gap-1 text-sm font-semibold text-primary hover:underline sm:flex">View all <ArrowRight className="h-4 w-4" /></Link>}
    </div>
    {children}
  </section>
);

export function useCategories() {
  const api = useApi();
  const [cats, setCats] = useState([]);
  useEffect(() => { api.get("/categories").then((r) => setCats(r.data)).catch(() => {}); }, [api]);
  return cats;
}

export const CatIcon = ({ name, className }) => { const I = Icons[name] || Icons.Code; return <I className={className} />; };

const STEPS = [
  [SearchIcon, "Discover", "Search services, sellers and digital products, or post a project and receive proposals."],
  [Handshake, "Pay securely", "Checkout runs on Stripe. Amounts and fees are calculated on our servers — never in your browser."],
  [PackageCheck, "Approve delivery", "Seller earnings are credited only after you accept the delivery or approve a milestone."],
];
const TRUST = [
  [ShieldCheck, "Verified reviews only", "Reviews can only be written by buyers with a completed order."],
  [Wallet, "Transparent fees", "A platform commission is deducted from seller earnings and shown before checkout."],
  [FileCheck2, "Private downloads", "Paid files are stored privately and every download is authorized server-side."],
  [Scale, "Refunds & disputes", "Request refunds or open disputes; our team reviews every case manually."],
];

export default function Home() {
  const api = useApi();
  const [d, setD] = useState(null);
  const cats = useCategories();
  useEffect(() => { api.get("/home").then((r) => setD(r.data)).catch(() => setD({ services: [], products: [], vendors: [], projects: [], testimonials: [], stats: {} })); }, [api]);
  const hp = d?.homepage || {};
  return (
    <div>
      <section className="relative overflow-hidden border-b border-border">
        <div className="grid-bg absolute inset-0 opacity-40 [mask-image:radial-gradient(ellipse_at_top_left,black,transparent_70%)]" />
        <div className="container-x relative grid gap-12 py-20 lg:grid-cols-12 lg:py-28">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="lg:col-span-7">
            <p className="eyebrow mb-5">Developer marketplace</p>
            <h1 className="text-4xl font-extrabold leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl" data-testid="hero-title">
              {hp.hero_title || <>Hire engineers. Ship software. <span className="text-primary">Sell what you build.</span></>}
            </h1>
            <p className="mt-6 max-w-xl text-base text-muted-foreground md:text-lg">
              {hp.hero_subtitle || "Services from freelance developers and agencies, project hiring with milestone contracts, and a store for source code, templates and SDKs — in one place."}
            </p>
            <div className="mt-8 max-w-xl"><GlobalSearch big testId="hero-search-input" /></div>
            <div className="mt-6 flex flex-wrap gap-3">
              <Button asChild size="lg" data-testid="hero-explore-cta"><Link to="/services">Explore services <ArrowRight className="ml-2 h-4 w-4" /></Link></Button>
              <Button asChild size="lg" variant="outline" data-testid="hero-post-project-cta"><Link to="/projects/new">Post a project</Link></Button>
            </div>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.15 }} className="lg:col-span-5">
            <div className="card-surface grid grid-cols-2 gap-px overflow-hidden bg-border p-0">
              {[["Sellers", d?.stats?.vendors], ["Live services", d?.stats?.services], ["Digital products", d?.stats?.products], ["Completed orders", d?.stats?.completed_orders]].map(([l, v]) => (
                <div key={l} className="bg-card p-6" data-testid={`hero-stat-${l.toLowerCase().replace(/ /g, "-")}`}>
                  <p className="font-mono text-3xl font-bold">{v ?? "—"}</p><p className="mt-1 text-sm text-muted-foreground">{l}</p>
                </div>
              ))}
              <div className="col-span-2 bg-card p-6 text-sm text-muted-foreground">
                Live counts from the marketplace database. Platform fee: <span className="font-semibold text-foreground">{d?.commission_pct ?? "—"}%</span> of each sale, paid by the seller.
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <Section eyebrow="Categories" title="Browse by discipline" to="/categories" testId="home-categories">
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
          {cats.slice(0, 12).map((c) => (
            <Link key={c.slug} to={`/services?category=${c.slug}`} data-testid={`category-tile-${c.slug}`} className="card-surface card-hover flex items-center gap-3 p-4">
              <CatIcon name={c.icon} className="h-5 w-5 text-primary" /><span className="truncate text-sm font-medium">{c.name}</span>
            </Link>
          ))}
        </div>
      </Section>

      {hp.show_trending_services !== false && (
        <Section eyebrow="Trending" title="Popular services" to="/services" testId="home-trending-services">
          {!d ? <GridSkeleton n={4} /> : <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">{d.services.map((s) => <ServiceCard key={s.id} s={s} />)}</div>}
        </Section>
      )}

      {hp.show_featured_vendors !== false && (
        <Section eyebrow="Talent" title="Featured developers & agencies" to="/developers" testId="home-featured-vendors">
          {!d ? <GridSkeleton n={3} /> : <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">{d.vendors.slice(0, 4).map((v) => <VendorCard key={v.id} v={v} />)}</div>}
        </Section>
      )}

      <section className="border-y border-border bg-[hsl(var(--surface))]">
        <div className="container-x grid gap-12 py-20 lg:grid-cols-12">
          <div className="lg:col-span-4"><p className="eyebrow mb-3">How it works</p><h2 className="text-2xl font-bold tracking-tight sm:text-3xl">From brief to shipped, with clear checkpoints.</h2></div>
          <div className="grid gap-6 sm:grid-cols-3 lg:col-span-8">
            {STEPS.map(([I, t, x], i) => (
              <div key={t} className="fade-up" style={{ animationDelay: `${i * 100}ms` }}>
                <div className="mb-4 flex items-center gap-3"><span className="font-mono text-sm text-muted-foreground">0{i + 1}</span><I className="h-5 w-5 text-primary" /></div>
                <h3 className="mb-2 font-semibold">{t}</h3><p className="text-sm text-muted-foreground">{x}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {hp.show_products !== false && (
        <Section eyebrow="Digital products" title="Source code, templates & SDKs" to="/products" testId="home-products">
          {!d ? <GridSkeleton n={4} /> : <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">{d.products.map((p) => <ProductCard key={p.id} p={p} />)}</div>}
        </Section>
      )}

      {hp.show_projects !== false && (
        <Section eyebrow="Open projects" title="Recently posted work" to="/projects" testId="home-projects">
          {!d ? <GridSkeleton n={2} /> : <div className="grid grid-cols-1 gap-6 md:grid-cols-2">{d.projects.map((p) => <ProjectCard key={p.id} p={p} />)}</div>}
        </Section>
      )}

      {d?.testimonials?.length > 0 && (
        <Section eyebrow="Verified reviews" title="What buyers say" testId="home-testimonials">
          <div className="grid gap-6 md:grid-cols-3">
            {d.testimonials.map((r) => (
              <figure key={r.id} className="card-surface p-6"><Quote className="mb-3 h-5 w-5 text-primary" /><blockquote className="text-sm">{r.comment}</blockquote>
                <figcaption className="mt-4 flex items-center justify-between text-xs text-muted-foreground"><span>{r.buyer_name} · {r.item_title}</span><Stars avg={r.rating} count={1} /></figcaption></figure>
            ))}
          </div>
        </Section>
      )}

      <Section eyebrow="Trust & safety" title="Built on verifiable rules" testId="home-trust">
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {TRUST.map(([I, t, x]) => <div key={t} className="card-surface p-6"><I className="mb-4 h-6 w-6 text-primary" /><h3 className="mb-2 font-semibold">{t}</h3><p className="text-sm text-muted-foreground">{x}</p></div>)}
        </div>
      </Section>

      <section className="container-x">
        <div className="flex flex-col items-start gap-6 rounded-2xl bg-[#0F172A] p-10 text-white md:flex-row md:items-center md:justify-between lg:p-14">
          <div><h2 className="text-2xl font-bold sm:text-3xl">Sell your services and code on DevMarket</h2><p className="mt-2 text-slate-300">Create a storefront in minutes. You keep {100 - (d?.commission_pct ?? 10)}% of every sale.</p></div>
          <Button asChild size="lg" className="bg-white text-slate-900 hover:bg-slate-100" data-testid="home-become-seller-cta"><Link to="/dashboard/vendor/storefront">Become a seller</Link></Button>
        </div>
      </section>
    </div>
  );
}

export { money };
